import { useQuery } from '@tanstack/react-query'

import { fetchAdminModules } from '@/api/modules'
import type { ResolvedModule } from '@/api/modules'

/**
 * Tüm modülleri cache'ler (TTL: 5 dk backend, her sayfada yeniden fetch yok).
 * Sadece auth:tenant middleware'den geçen isteklerde çalışır.
 */
export function useModules() {
  return useQuery({
    queryKey: ['admin-modules'],
    queryFn: fetchAdminModules,
    staleTime: 5 * 60 * 1000, // 5 dk — backend cache ile uyumlu
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Tek bir modülün durumunu ve limitlerini döner.
 *
 * @example
 * const { isEnabled, limit, usage } = useModule('marketplace')
 * if (!isEnabled) return <DisabledPlaceholder />
 */
export function useModule(moduleKey: string) {
  const { data, isLoading } = useModules()

  const mod: ResolvedModule | undefined = data?.modules.find((m) => m.key === moduleKey)

  return {
    /** Modül verisi yükleniyor mu? */
    isLoading,
    /** Modül bu tenant için etkin mi? */
    isEnabled: mod?.enabled ?? false,
    /** Kaynak: 'override' | 'plan' | 'default' */
    source: mod?.source ?? 'default',
    /** Geçerlilik bitiş tarihi (override ise) */
    expiresAt: mod?.expiresAt ?? null,
    /**
     * Belirli bir limit anahtarının değerini döner.
     * @example limit('max_count') → 50
     */
    limit: (key: string, fallback = 0): number => mod?.limits[key] ?? fallback,
    /**
     * Etkin limit (backend çözümlemesi varsa öncelikli).
     */
    effectiveLimit: (key: string, fallback = 0): number => {
      const effective = mod?.effectiveLimits?.[key]
      if (typeof effective === 'number' && effective > 0) return effective
      const raw = mod?.limits[key]
      if (typeof raw === 'number' && raw > 0) return raw
      return fallback
    },
    /** Ham modül kaydı */
    raw: mod,
  }
}

/**
 * Etkin modüllerin key listesini döner.
 * Sidebar menü filtresi için kullanışlı.
 */
export function useEnabledModuleKeys(): string[] {
  const { data } = useModules()
  return data?.enabledKeys ?? []
}

/**
 * Tenant tipini döner: 'store' | 'website'.
 * Admin paneli buna göre route/menu daraltması yapar.
 */
export function useTenantType(): string {
  const { data } = useModules()
  return data?.tenantType ?? 'store'
}
